// src/components/FooterPremium.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./FooterPremium.css";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const FOOTER_COPY = {
  es: {
    legalTitle: "Legal",
    contactTitle: "Contacto",
    newsTitle: "ZEDRO News",
    legalLinks: {
      legalNotice: "Aviso legal",
      privacy: "Política de privacidad",
      cookies: "Política de cookies",
      terms: "Términos y condiciones",
      cancellationRefunds: "Cancelación y reembolsos",
      disclaimer: "Descargo de responsabilidad",
      cookiePreferences: "Preferencias de cookies",
    },
    newsletterText:
      "Recibe ideas, recursos y novedades para ordenar tu interior, fortalecer tus decisiones y construir con propósito.",
    emailLabel: "Email",
    emailPlaceholder: "Tu correo electrónico",
    newsletterButton: "Suscríbete",
    newsletterNote:
      "Al suscribirte, aceptas recibir ZEDRO News por correo. Puedes darte de baja cuando quieras.",
    brandTextPublic: "Formación con raíz eterna",
    brandTextApp: "Formación con raíz eterna",
    socialAria: "Redes sociales",
    alerts: {
      already: "Ya estás suscrito a ZEDRO News.",
      reactivated: "Tu suscripción a ZEDRO News fue reactivada.",
      subscribed: "Gracias por suscribirte a ZEDRO News.",
      error: "No fue posible completar la suscripción. Intenta de nuevo.",
    },
    copyright: "Todos los derechos reservados.",
  },
  fr: {
    legalTitle: "Mentions légales",
    contactTitle: "Contact",
    newsTitle: "ZEDRO News",
    legalLinks: {
      legalNotice: "Avis légal",
      privacy: "Politique de confidentialité",
      cookies: "Politique de cookies",
      terms: "Conditions générales",
      cancellationRefunds: "Annulation et remboursements",
      disclaimer: "Avis de non-responsabilité",
      cookiePreferences: "Préférences de cookies",
    },
    newsletterText:
      "Recevez des idées, des ressources et des nouvelles pour mettre de l’ordre dans votre vie intérieure, affermir vos décisions et construire avec intention.",
    emailLabel: "Courriel",
    emailPlaceholder: "Votre adresse courriel",
    newsletterButton: "S’abonner",
    newsletterNote:
      "En vous abonnant, vous acceptez de recevoir ZEDRO News par courriel. Vous pouvez vous désabonner à tout moment.",
    brandTextPublic: "Formation aux racines éternelles",
    brandTextApp: "Formation aux racines éternelles",
    socialAria: "Réseaux sociaux",
    alerts: {
      already: "Vous êtes déjà abonné à ZEDRO News.",
      reactivated: "Votre abonnement à ZEDRO News a été réactivé.",
      subscribed: "Merci de vous être abonné à ZEDRO News.",
      error: "L’abonnement n’a pas pu être effectué. Veuillez réessayer.",
    },
    copyright: "Tous droits réservés.",
  },
};

export default function FooterPremium({ variant = "public" }) {
  const year = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [language, setLanguage] = useState(getInitialLanguage);

  const API_URL = (
    process.env.REACT_APP_API_URL || "https://zedro-backend.onrender.com"
  ).replace(/\/$/, "");
  const copy = FOOTER_COPY[language] || FOOTER_COPY.es;

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      } else {
        setLanguage("es");
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const LEGAL = (
    <>
      <li>
        <Link to="/legal/aviso-legal" state={{ from: variant }}>
          {copy.legalLinks.legalNotice}
        </Link>
      </li>
      <li>
        <Link to="/legal/privacidad" state={{ from: variant }}>
          {copy.legalLinks.privacy}
        </Link>
      </li>
      <li>
        <Link to="/legal/cookies" state={{ from: variant }}>
          {copy.legalLinks.cookies}
        </Link>
      </li>
      <li>
        <Link to="/legal/terminos" state={{ from: variant }}>
          {copy.legalLinks.terms}
        </Link>
      </li>
      <li>
        <Link to="/legal/cancelacion-reembolsos" state={{ from: variant }}>
          {copy.legalLinks.cancellationRefunds}
        </Link>
      </li>
      <li>
        <Link to="/legal/descargo" state={{ from: variant }}>
          {copy.legalLinks.disclaimer}
        </Link>
      </li>
      <li>
        <button
          type="button"
          onClick={() =>
            window.dispatchEvent(new CustomEvent("open-cookie-settings"))
          }
          className="zf-linklike"
        >
          {copy.legalLinks.cookiePreferences}
        </button>
      </li>
    </>
  );

  const CONTACT = (
    <>
      <li>
        <a href="mailto:hello@zedro.academy">hello@zedro.academy</a>
      </li>
      <li>
        <a href="mailto:support@zedro.academy">support@zedro.academy</a>
      </li>
    </>
  );

  const BRAND_TEXT =
    variant === "public" ? copy.brandTextPublic : copy.brandTextApp;

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    if (!email) return;

    try {
      const res = await fetch(`${API_URL}/api/subscribers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "Error");
      }

      if (data.already) {
        alert(copy.alerts.already);
      } else if (data.reactivated) {
        alert(copy.alerts.reactivated);
      } else {
        alert(copy.alerts.subscribed);
      }

      setEmail("");
    } catch (err) {
      console.error(err);
      alert(copy.alerts.error);
    }
  };

  return (
    <footer
      className={`zedro-footer-premium ${
        variant === "app" ? "zedro-footer-premium--app" : ""
      }`}
    >
      <div className="zf-wrap">
        <div className="zf-left">
          <div className="zf-colgroup">
            <div className="zf-col">
              <h4>{copy.legalTitle}</h4>
              <ul>{LEGAL}</ul>
            </div>

            <div className="zf-col">
              <h4>{copy.contactTitle}</h4>
              <ul>{CONTACT}</ul>
            </div>

            <div className="zf-col">
              <h4>{copy.newsTitle}</h4>

              <p className="zf-news-text">{copy.newsletterText}</p>

              <form className="zf-news-form" onSubmit={handleNewsletterSubmit}>
                <label className="zf-sr-only" htmlFor="zedro-news-email">
                  {copy.emailLabel}
                </label>

                <input
                  id="zedro-news-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder={copy.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />

                <button type="submit">{copy.newsletterButton}</button>
              </form>

              <small className="zf-news-note">{copy.newsletterNote}</small>
            </div>
          </div>

          <div className="zf-accordions">
            <details>
              <summary>{copy.legalTitle}</summary>
              <ul className="zf-acc-list">{LEGAL}</ul>
            </details>

            <details>
              <summary>{copy.contactTitle}</summary>
              <ul className="zf-acc-list">{CONTACT}</ul>
            </details>

            <details>
              <summary>{copy.newsTitle}</summary>
              <div className="zf-acc-panel">
                <p className="zf-news-text">{copy.newsletterText}</p>

                <form className="zf-news-form" onSubmit={handleNewsletterSubmit}>
                  <label className="zf-sr-only" htmlFor="zedro-news-email-m">
                    {copy.emailLabel}
                  </label>

                  <input
                    id="zedro-news-email-m"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder={copy.emailPlaceholder}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />

                  <button type="submit">{copy.newsletterButton}</button>
                </form>

                <small className="zf-news-note">{copy.newsletterNote}</small>
              </div>
            </details>
          </div>
        </div>

        <div className="zf-brand">
          <div
            className="zf-brandMark"
            role="img"
            aria-label="ZEDRO Academy"
          >
            <div className="zf-wordmark" aria-hidden="true">
              Z E D R O
            </div>

            <div className="zf-academy" aria-hidden="true">
              A C A D E M Y
            </div>
          </div>

          <small className="zf-tagline">{BRAND_TEXT}</small>

          <div className="zf-social" aria-label={copy.socialAria}>
            <a
              aria-label="Instagram ZEDRO Academy"
              href="https://www.instagram.com/zedro.academy/"
              target="_blank"
              rel="noreferrer"
              className="zf-social-link"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <rect
                  x="6"
                  y="6"
                  width="12"
                  height="12"
                  rx="3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="3.2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <circle cx="16.4" cy="7.6" r="1" fill="currentColor" />
              </svg>
              <span>Instagram</span>
            </a>
          </div>
        </div>
      </div>

      <div className="zf-copy">
        <small>
          © {year} Académie ZEDRO — ZEDRO Academy. {copy.copyright}
        </small>
      </div>
    </footer>
  );
}